import React from "react";
import PropTypes from "prop-types";
import "./index.css";

const OrderProgressStepper = ({ status, orderType, carrier }) => {
  // Define all possible statuses with their visual properties
  const statusConfig = {
    NOT_STARTED: { title: "NOT STARTED", color: "#9ca3af", icon: "⏳" },
    ASSESSMENT_REQUIRED: { title: "ASSESSMENT REQUIRED", color: "#6ecff6", icon: "📋" },
    PENDING: { title: "PENDING REVIEW", color: "#f59e0b", icon: "🔍" },
    CANCELED: { title: "CANCELED", color: "#ef4444", icon: "❌" },
    READY_FOR_RM: { title: "READY FOR RM", color: "#a855f7", icon: "✅" },
    PRIME_READY: { title: "PRIME READY", color: "#3b82f6", icon: "⭐" },
    SEND_TO_RM: { title: "SENT TO RM", color: "#3b82f6", icon: "✈️" },
    SHIPPED: { title: "SHIPPED", color: "#f59e0b", icon: "🚚" },
    DELIVERED: { title: "DELIVERED", color: "#10b981", icon: "🏠" }
  };

  // Determine the appropriate status flow based on order type and carrier
  const getStatusFlow = () => {
    if (orderType === "P-Med") {
      return [
        "NOT_STARTED",
        "ASSESSMENT_REQUIRED",
        "PENDING",
        "READY_FOR_RM",
        "SEND_TO_RM",
        "SHIPPED",
        "DELIVERED"
      ];
    } else if (orderType === "P-CAT-D") {
      return [
        "NOT_STARTED",
        "PENDING",
        "READY_FOR_RM",
        "SEND_TO_RM",
        "SHIPPED",
        "DELIVERED"
      ];
    } else if (carrier === "AMZ" || carrier === "Amazon") {
      return [
        "NOT_STARTED",
        "PRIME_READY",
        "SHIPPED",
        "DELIVERED"
      ];
    } else { // GSL orders
      return [
        "NOT_STARTED",
        "READY_FOR_RM",
        "SEND_TO_RM",
        "SHIPPED",
        "DELIVERED"
      ];
    }
  };

  // Get the base status flow
  let statusFlow = getStatusFlow();

  // Handle CANCELED status insertion
  if (status === "CANCELED") {
    const cancelIndex = (orderType === "P-Med" || orderType === "P-CAT-D") && statusFlow.includes("PENDING")
      ? statusFlow.indexOf("PENDING") + 1
      : statusFlow.indexOf(status) > -1 
        ? statusFlow.indexOf(status)
        : statusFlow.length;
    statusFlow.splice(cancelIndex, 0, "CANCELED");
  }

  // Find current position in the flow
  const currentIndex = statusFlow.indexOf(status);
  
  // Always show from start to current status + next possible status
  const visibleStatuses = currentIndex >= 0 
    ? statusFlow.slice(0, Math.min(currentIndex + 2, statusFlow.length))
    : statusFlow;

  return (
    <div className="card rounded-4 p-4 mb-3  d-none d-sm-block">
      <div className="stepper-container">
        {visibleStatuses.map((statusKey, index) => {
          const step = statusConfig[statusKey];
          const isActive = statusKey === status;
          const isCompleted = currentIndex >= 0 && statusFlow.indexOf(statusKey) < currentIndex;
          const isCanceled = statusKey === "CANCELED";

          return (
            <React.Fragment key={statusKey}>
              <div className="step-item">
                <div
                  className={`step-bubble ${
                    isCompleted ? "completed" : isActive ? "active" : "pending"
                  } ${isCanceled ? "canceled" : ""}`}
                  style={{ backgroundColor: isCanceled ? "#ef4444" : step.color }}
                >
                  {isCompleted && !isCanceled ? (
                    <svg
                      className="checkmark"
                      width="14"
                      height="14"
                      viewBox="0 0 24 24"
                      fill="none"
                      stroke="currentColor"
                      strokeWidth="3"
                    >
                      <path d="M20 6L9 17l-5-5" />
                    </svg>
                  ) : (
                    <span className="step-icon">{step.icon}</span>
                  )}
                </div>
                <div className={`step-label ${isActive || isCompleted ? "active" : "inactive"}`}>
                  {step.title}
                </div>
              </div>
              {index < visibleStatuses.length - 1 && (
                <div
                  className={`connector-line ${
                    isCompleted ? "completed" : "pending"
                  } ${isCanceled ? "canceled-line" : ""}`}
                />
              )}
            </React.Fragment>
          );
        })}
      </div>
    </div>
  );
};

OrderProgressStepper.propTypes = {
  status: PropTypes.string.isRequired,
  orderType: PropTypes.oneOf(["P-Med", "P-CAT-D", "GSL", "OTHER"]),
  carrier: PropTypes.string
};

export default OrderProgressStepper;